import { useMemo } from "react"
import type { ApolloError, WatchQueryFetchPolicy } from "@apollo/client"
import { useToast } from "@mallardbay/lib-react-components"

import {
    useOutfitterQuery,
    useRemoveOutfitterMutation,
} from "~graphql/outfitters.generated"
import { namedOperations } from "~graphql/generated/named-operations"
import type { RemoveOutfitterMutation } from "~graphql/outfitters.generated"
import { useUser } from "~utils/hooks/use-user"
import COPY from "~config/copy-constants"
import { filterApprovedOutfitters } from "~utils/helpers/helpers"
import { usePortalEntitiesContext } from "~contexts/outfitter-provider"

interface Props {
    readonly id?: string | null
    readonly fetchPolicy?: WatchQueryFetchPolicy
}

export default function useOutfitter({ id, fetchPolicy = "no-cache" }: Props) {
    const { showError } = useToast()

    const {
        data,
        loading: isLoading,
        error,
        refetch,
    } = useOutfitterQuery({
        onError: showError,
        variables: { id: id ?? "" },
        fetchPolicy,
        skip: !id,
    })

    const outfitter = data?.outfitter ?? null

    return { outfitter, isLoading, error, refetch }
}

export function useDeleteOutfitter({
    id,
    onSuccessCallback,
    onErrorCallback,
}: {
    id: string
    onSuccessCallback?: (data: RemoveOutfitterMutation) => void
    onErrorCallback?: (error: ApolloError) => void
}) {
    const { showSuccess, showError } = useToast()
    const successMessage = COPY.DELETE_OUTFITTER_SUCCESS
    const errorMessage = COPY.DELETE_OUTFITTER_ERROR

    return useRemoveOutfitterMutation({
        variables: { id },
        onCompleted: (data) => {
            showSuccess(successMessage)
            onSuccessCallback?.(data)
        },
        onError: (error) => {
            showError(new Error(errorMessage))
            onErrorCallback?.(error)
        },
        refetchQueries: [
            namedOperations.Query.portalEntities,
            namedOperations.Query.searchOutfitters,
        ],
        awaitRefetchQueries: true,
    })
}

export function useMyOutfitters() {
    const { isLoading: isUserLoading } = useUser()

    const { outfitters, isLoading, error } = usePortalEntitiesContext()

    const approvedOutfitters = useMemo(
        () => filterApprovedOutfitters(outfitters),
        [outfitters]
    )

    return {
        error,
        outfitters,
        approvedOutfitters,
        defaultOutfitter: outfitters[0] ?? null,
        isLoading: isUserLoading || isLoading,
    }
}
